import React from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  InputNumber,
  Switch,
  Typography,
  Row,
  Col,
  Button,
  Alert,
  Tooltip,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  AssetIngressValidationConfigInput,
  AssetIngressValidationConfigOutput,
  SiteIngressValidationConfigInput,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import { configureSiteMutation } from "../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../util-components/Notifications/notifications";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Title, Paragraph, Text } = Typography;

const DEFAULT_MAIN_COLLECTOR_MINUTES = 125;
const DEFAULT_WITH_TRANSACTION_MINUTES = 5;
const DEFAULT_IDLE_MINUTES = 125;

const secondsToMinutes = (seconds?: number | null): number | null =>
  seconds == null ? null : Math.round((seconds / 60) * 100) / 100;

const minutesToIso = (minutes: number): string =>
  `PT${Math.round(minutes * 60)}S`;

const assetOutputToInput = (
  asset: AssetIngressValidationConfigOutput,
): AssetIngressValidationConfigInput => ({
  with_transaction_expected_measurement_interval:
    asset.with_transaction_expected_measurement_interval != null
      ? minutesToIso(asset.with_transaction_expected_measurement_interval / 60)
      : undefined,
  without_transaction_expected_measurement_interval:
    asset.without_transaction_expected_measurement_interval != null
      ? minutesToIso(
          asset.without_transaction_expected_measurement_interval / 60,
        )
      : asset.without_transaction_expected_measurement_interval,
});

type FormValues = {
  mainCollectorValidated: boolean;
  mainCollectorMinutes: number;
  withTransactionMinutes: number;
  idleValidated: boolean;
  idleMinutes: number;
};

type IngressValidationFormProps = {
  site: Site;
  refetchSite: () => void;
};

export const IngressValidationForm: React.FC<IngressValidationFormProps> = ({
  site,
  refetchSite,
}) => {
  const [form] = useForm<FormValues>();
  const notify = useNotify();
  const isViewer = useIsViewer();

  const mainCollectorValidated = Form.useWatch("mainCollectorValidated", form);
  const idleValidated = Form.useWatch("idleValidated", form);
  const withTransactionMinutes = Form.useWatch("withTransactionMinutes", form);

  const configureSite = useMutation({
    ...configureSiteMutation(),
  });

  const ingress = site.configurations?.ingress_validation;

  const initialValues: FormValues = {
    mainCollectorValidated:
      ingress?.main_collector_expected_measurement_interval !== null,
    mainCollectorMinutes:
      secondsToMinutes(ingress?.main_collector_expected_measurement_interval) ??
      DEFAULT_MAIN_COLLECTOR_MINUTES,
    withTransactionMinutes:
      secondsToMinutes(
        ingress?.asset?.with_transaction_expected_measurement_interval,
      ) ?? DEFAULT_WITH_TRANSACTION_MINUTES,
    idleValidated:
      ingress?.asset?.without_transaction_expected_measurement_interval !==
      null,
    idleMinutes:
      secondsToMinutes(
        ingress?.asset?.without_transaction_expected_measurement_interval,
      ) ?? DEFAULT_IDLE_MINUTES,
  };

  const onFinish = (values: FormValues) => {
    const preservedPerAssetType = ingress?.per_asset_type
      ? Object.fromEntries(
          Object.entries(ingress.per_asset_type).map(([assetType, config]) => [
            assetType,
            assetOutputToInput(config),
          ]),
        )
      : undefined;

    const ingressValidation: SiteIngressValidationConfigInput = {
      main_collector_expected_measurement_interval:
        values.mainCollectorValidated
          ? minutesToIso(values.mainCollectorMinutes)
          : null,
      asset: {
        with_transaction_expected_measurement_interval: minutesToIso(
          values.withTransactionMinutes,
        ),
        without_transaction_expected_measurement_interval: values.idleValidated
          ? minutesToIso(values.idleMinutes)
          : null,
      },
      ...(preservedPerAssetType
        ? { per_asset_type: preservedPerAssetType }
        : {}),
    };

    configureSite.mutate(
      {
        path: { site_id: site.site_id },
        body: { ingress_validation: ingressValidation },
      },
      {
        onError: () => {
          notify({
            type: "error",
            message:
              "Something went wrong while updating the measurement validation.",
          });
        },
        onSuccess: () => {
          refetchSite();
          notify({
            type: "success",
            message: "Measurement validation updated successfully!",
          });
        },
      },
    );
  };

  return (
    <div className="mt-8">
      <Title level={3}>Measurement Validation</Title>
      <Paragraph type="secondary" className="mb-4">
        Controls how often we expect to receive measurements before flagging an
        asset as "not receiving data". Raise these intervals to match how often
        your assets report. Increasing an interval silences the warning but can
        reduce optimization accuracy.
      </Paragraph>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        layout="vertical"
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
        disabled={isViewer}
      >
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Form.Item
              name="mainCollectorValidated"
              label="Validate main collector"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="mainCollectorMinutes"
              label="Main collector interval"
              rules={
                mainCollectorValidated
                  ? [{ required: true, message: "Please enter an interval" }]
                  : undefined
              }
            >
              <InputNumber
                min={1}
                addonAfter="min"
                style={{ width: "100%" }}
                disabled={!mainCollectorValidated}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} align="middle">
          <Col span={8}>
            <Form.Item label="Validate active assets">
              <Tooltip title="This check is always enabled.">
                <span>
                  <Switch checked disabled />
                </span>
              </Tooltip>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="withTransactionMinutes"
              label="Asset interval during active transaction"
              rules={[{ required: true, message: "Please enter an interval" }]}
            >
              <InputNumber min={1} addonAfter="min" style={{ width: "100%" }} />
            </Form.Item>
            {withTransactionMinutes > 5 && (
              <Text type="danger">
                Note: the optimization works best with measurements at least
                every 5 minutes during active transactions.
              </Text>
            )}
          </Col>
        </Row>

        <Row gutter={16} align="middle">
          <Col span={8}>
            <Form.Item
              name="idleValidated"
              label="Validate idle assets"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="idleMinutes"
              label="Asset interval when idle"
              rules={
                idleValidated
                  ? [{ required: true, message: "Please enter an interval" }]
                  : undefined
              }
            >
              <InputNumber
                min={1}
                addonAfter="min"
                style={{ width: "100%" }}
                disabled={!idleValidated}
              />
            </Form.Item>
          </Col>
        </Row>

        <DisableForViewer>
          <Button
            type="primary"
            htmlType="submit"
            loading={configureSite.isPending}
            className="mt-4"
          >
            Update Measurement Validation
          </Button>
        </DisableForViewer>

        {configureSite.isError && (
          <Alert
            message="Error"
            description="Something went wrong while updating the measurement validation."
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
    </div>
  );
};

export default IngressValidationForm;
